import { default as Select } from 'antd/es/select';
import { default as Spin } from 'antd/es/spin';
import React, { useEffect, useMemo, useState } from 'react';
import { EndpointPath } from '../components/EndpointPath';
import { CustomPayoutSchema } from '../components/endpoint/CustomPayoutSchema';
import { type ProductSchema } from '../components/endpoint/types';
import { PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES } from '../data/createPayoutData/payoutSchemasAndExamples';

import {
  type ProductFromNetwork,
  type ProductLabel
} from '../data/createPayoutData/types';
import { FormattedProduct } from '../utils/FormattedProduct';

import { useUser } from '../hooks/useUser';
import { fetchProducts } from '../network/fetchProducts';
import { useProductIdFromUrl } from './useProductIdFromUrl';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';

export function CreatePayoutPage() {
  const [selectedProductId, setSelectedProductId] = React.useState<number>(
    PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES?.[0].data.schema.payout_product.id
  );
  const [schemaId, setSchemaId] = useState<string | undefined>(undefined);
  const [products, setProducts] = useState<Array<Partial<ProductFromNetwork>>>(
    []
  );

  const [loadingProducts, setLoadingProducts] = useState(false);

  const productId = useProductIdFromUrl();

  const { currentOrganisation } = useUser();

  useEffect(() => {
    const getProducts = async () => {
      setLoadingProducts(true);

      if (currentOrganisation) {
        const products = await fetchProducts(
          currentOrganisation.id.toString(),
          'disbursement'
        );
        if (products?.data) {
          setProducts(products.data);
          syncUrlWithSelectedProductId(productId ?? products.data?.[0]?.id);
          setSelectedProductId(productId ?? products.data?.[0]?.id);
        }
      }
      setLoadingProducts(false);
    };
    getProducts();
  }, [currentOrganisation, productId]);

  const payoutSchemas = useMemo(() => {
    if ((products?.length ?? 0) > 0) {
      return products.map((product) => {
        return {
          key: product.id,
          label: (
            <FormattedProduct
              countryIso={product.country_iso ?? ''}
              label={product.label ?? ''}
            />
          ),
          value: product.id ?? 0,
          index: product.label ?? ''
        };
      });
    } else {
      return PAYOUT_SCHEMAS_AND_EXAMPLES_SAMPLES.map((product) => {
        return {
          key: product.data.schema.payout_product.id,
          label: (
            <FormattedProduct
              countryIso={product.data.schema.payout_product.country}
              label={createProductIndexString(product.data.schema)}
            />
          ),
          value: product.data.schema.payout_product.id,
          index: createProductIndexString(product.data.schema)
        };
      });
    }
  }, [products]);

  const filterOption = (
    input: string,
    option?: { label: JSX.Element; value: number; index: string }
  ) =>
    (option?.index ?? '')
      .toLocaleLowerCase()
      .includes(input.toLocaleLowerCase());

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <EndpointPath path="/disbursement/payment_requests" method="POST" />
        <div className="flex flex-row flex-wrap items-center justify-between xl:flex-nowrap xl:gap-8">
          <div className="xl:w-1/2">
            <p>
              Makes a new payment request. The request parameters might vary
              depending on the payment method, creditor country and type. You
              can check the required fields under the products section
            </p>
          </div>
          <div className="flex max-h-12 w-full xl:w-1/2">
            {loadingProducts && currentOrganisation ? (
              <Spin />
            ) : (
              <Select
                className="w-full"
                showSearch
                placeholder="Select a product"
                optionFilterProp="children"
                filterOption={filterOption}
                onChange={(newItem) => {
                  syncUrlWithSelectedProductId(Number(newItem));
                  setSelectedProductId(Number(newItem));
                  setSchemaId(undefined);
                }}
                defaultValue={
                  payoutSchemas.find((x) => {
                    return x.value === selectedProductId;
                  }) ?? payoutSchemas?.[0]
                }
                options={payoutSchemas}
                size="large"
              />
            )}
          </div>
        </div>

        <CustomPayoutSchema
          organisationId={currentOrganisation?.id.toString() ?? undefined}
          setSchemaId={setSchemaId}
          schemaId={schemaId}
          selectedProductId={selectedProductId}
        />
      </div>
    </div>
  );
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const currencyNames = new Intl.DisplayNames(['en'], { type: 'currency' });
/**
 * helper function
 */
function createProductIndexString(
  schema: ProductSchema
): ProductLabel | string {
  if (schema.payout_product) {
    return `(${schema.payout_product?.currency}) ${currencyNames.of(schema.payout_product?.currency ?? '')} & (${schema.payout_product?.country}) ${regionNames.of(schema.payout_product?.country ?? '')} - ${schema.current_schema?.name}`;
  }

  return '';
}

/**
 *
 * Syncs the url with the product_id so that user can deep link things
 */
function syncUrlWithSelectedProductId(newProductId: number) {
  const currentUrl = new URL(window.location.href);
  const currentHash = currentUrl.hash.split('?')[0];

  if (currentHash !== '#payouts-create') return;

  // if magic token is there, make sure that it's added
  const enhancedLink = enhanceLinkWithToken(
    `${currentUrl.origin}${currentUrl.pathname}${currentHash}?product_id=${newProductId}`
  );

  window.history.replaceState(window.history.state, '', enhancedLink);
}
